import type { components } from '@connected/api-client/api.generated';
import type { RadioGroupOption } from '@connected/form-fields-v2/components/form/radio-group';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { FileInput } from '@connected/ui/components/file';
import { Popout, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Observation } from '@/components/observation';
import { PhoneInput } from '@/components/phone-input';
import { useUserSignature } from '@/features/user-settings/hooks/use-user-signature';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { cn } from '@/lib/utils';

type FormValues = components['schemas']['CreateUserCommand'] & {
    File?: File | null;
};

type RoleOptionsDefault = components['schemas']['CreateOrganizationUserQueryResultRole'];
export type UserData = components['schemas']['ListUserQueryResultUser'];

type UserSettingsModalProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    roles?: { Roles?: RoleOptionsDefault[] | null };
    user?: UserData;
    organizationId: string;
    onSuccess?: () => void;
};

const standardColors = ['#099250', '#1570EF', '#444CE7', '#6938EF', '#fcc200', '#008080', '#b22222', '#0F172B', '#C81CDE'];

export function UserSettingsModal({
    isOpen,
    setIsOpen,
    roles,
    user,
    organizationId,
    onSuccess,
}: UserSettingsModalProps) {
    const { t } = useTranslation('dashboard');

    const [loading, setIsLoading] = useState<boolean>(false);

    const signature = useUserSignature({
        userId: user?.UserId ?? '',
        enabled: isOpen && !!user?.UserId,
    });

    const organisationRoles = useQuery(elektraAppQueryOptions.organisation.management.organisationRolesGetOptions({
        organisationId: organizationId,
    }));

    const DEFAULT_VALUES: FormValues = useMemo(() => ({
        Email: user?.Email ?? '',
        RoleIds: (user?.Roles?.map(role => role?.RoleId).filter((id): id is string => Boolean(id)) ?? []) as string[],
        Settings: {
            DisplayName: user?.Settings?.DisplayName ?? '',
            SignatureText: user?.Settings?.SignatureText ?? '',
            FirstName: user?.Settings?.FirstName ?? '',
            MiddleName: user?.Settings?.MiddleName ?? '',
            LastName: user?.Settings?.LastName ?? '',
            Department: user?.Settings?.Department ?? '',
            JobTitleDescription: user?.Settings?.JobTitleDescription ?? '',
            PhoneNumber: user?.Settings?.PhoneNumber ?? '',
            ColorCode: user?.Settings?.ColorCode ?? '',
            File: null,
        },
    }), [user]);

    const roleOptions: RadioGroupOption<string>[] = useMemo(
        () =>
            roles?.Roles?.map((row: { RoleId: string; Name?: string | null }) => ({
                value: row.RoleId,
                label: row.Name ?? '',
            })) ?? [],
        [roles],
    );

    const createNewUser = api.useMutation(
        'post',
        '/api/v2/users/organizations/create',
        {
            onSuccess: (message) => {
                if (message?.Result === 'EmailInUse') {
                    toast.error(t('dashboard.users.message.emailInUse'));
                    return;
                }
                toast.success(t('dashboard.users.message.createSuccess'));
                queryClient.invalidateQueries(elektraAppQueryOptions.organisation.management.usersListOptions({ organisationId: organizationId })).then(() => {
                    setIsLoading(false);
                    setIsOpen(false);
                    onSuccess?.();
                });
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'CreateOrganizationUser',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/users/organizations/create' },
                );
                toast.error(error?.Message ?? t('dashboard.users.message.createFail'));
            },
        },
    );

    const RemoveRoleFromUser = api.useMutation(
        'put',
        '/api/v2/users/roles/remove',
        {
            onError: (error) => {
                logger.error(
                    {
                        system: 'RemoveRoleFromUser',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/users/roles/remove' },
                );
                toast.error(error?.Message ?? t('dashboard.users.message.deleteRoleFail'));
            },
        },
    );

    const UpdateUser = api.useMutation(
        'put',
        '/api/v2/users/settings/edit',
        {
            onError: (error) => {
                logger.error(
                    {
                        system: 'EditUserSettingsCommand',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/users/settings/edit' },
                );
                toast.error(error?.Message ?? t('dashboard.users.message.deleteRoleFail'));
            },
        },
    );

    const AddRoleToUser = api.useMutation(
        'put',
        '/api/v2/users/roles/add',
        {
            onError: (error) => {
                logger.error(
                    {
                        system: 'AddRoleToUserCommand',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/users/roles/add' },
                );
                toast.error(error?.Message ?? t('dashboard.users.message.deleteRoleFail'));
                setIsLoading(false);
            },
            onSuccess: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.organisation.management.usersListOptions({ organisationId: organizationId })).then(() => {
                    setIsOpen(false);
                });
            },
        },
    );

    const updateUserEmail = api.useMutation(
        'post',
        '/api/v2/users/emails/edit',
        {
            onError: (error) => {
                logger.error(
                    {
                        system: 'ChangeUserEmailCommand',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/users/emails/edit' },
                );
                toast.error(error?.Message ?? t('dashboard.users.message.updateFail'));
            },
            onSuccess: () => {
                toast.success(t('dashboard.users.message.updateSuccess'));
                queryClient.invalidateQueries(elektraAppQueryOptions.organisation.management.usersListOptions({ organisationId: organizationId })).then(() => {
                    setIsOpen(false);
                    onSuccess?.();
                });
            },
        },
    );

    const form = useAppForm({
        defaultValues: DEFAULT_VALUES,
        onSubmit: async ({ value }) => {
            try {
                setIsLoading(true);

                if (!user) {
                    await createNewUser.mutateAsync({
                        body: {
                            OrganizationId: organizationId,
                            Email: value.Email,
                            RoleIds: value.RoleIds,
                            Settings: {
                                DisplayName: value.Settings?.DisplayName,
                                SignatureText: value.Settings?.SignatureText,
                                FirstName: value.Settings?.FirstName,
                                MiddleName: value.Settings?.MiddleName,
                                LastName: value.Settings?.LastName,
                                Department: value.Settings?.Department,
                                JobTitleDescription: value.Settings?.JobTitleDescription,
                                ColorCode: value.Settings?.ColorCode,
                                PhoneNumber: value.Settings?.PhoneNumber,
                            },
                        },
                    }).then(async (createdUser) => {
                        if (signature.selectedFile) {
                            await signature.handleSignatureSubmit(createdUser.UserId ?? '');
                        }
                    });
                    return;
                }

                await UpdateUser.mutateAsync({
                    body: {
                        DisplayName: value.Settings?.DisplayName,
                        SignatureText: value.Settings?.SignatureText,
                        FirstName: value.Settings?.FirstName,
                        MiddleName: value.Settings?.MiddleName,
                        LastName: value.Settings?.LastName,
                        Department: value.Settings?.Department,
                        JobTitleDescription: value.Settings?.JobTitleDescription,
                        ColorCode: value.Settings?.ColorCode,
                        PhoneNumber: value.Settings?.PhoneNumber,
                        UserId: user.UserId ?? '',
                    },
                });

                await signature.handleSignatureSubmit(user.UserId ?? '');

                if (user.UserId) {
                    await queryClient.invalidateQueries(
                        portaalQueryOptions.electrical.users.userFiles({
                            userId: user.UserId,
                        }),
                    );
                }

                if (DEFAULT_VALUES.RoleIds.length > 0) {
                    await RemoveRoleFromUser.mutateAsync({
                        body: {
                            RoleIds: DEFAULT_VALUES.RoleIds,
                            UserId: user.UserId ?? '',
                        },
                    });
                }
                await AddRoleToUser.mutateAsync({
                    body: {
                        RoleIds: value.RoleIds,
                        UserId: user.UserId ?? '',
                    },
                });

                if (DEFAULT_VALUES.Email !== value.Email) {
                    await updateUserEmail.mutateAsync({
                        body: {
                            Email: value.Email,
                            UserId: user.UserId ?? '',
                        },
                    });
                }

                if (DEFAULT_VALUES.Email === value.Email) {
                    await queryClient.invalidateQueries(
                        elektraAppQueryOptions.organisation.management.usersListOptions({ organisationId: organizationId }),
                    );
                    toast.success(t('dashboard.users.message.updateSuccess'));
                    setIsOpen(false);
                    onSuccess?.();
                }
            }
            finally {
                setIsLoading(false);
            }
        },
    });

    useEffect(() => {
        if (isOpen) {
            form.reset(DEFAULT_VALUES);
            signature.resetState();
        }
    }, [isOpen, user, DEFAULT_VALUES, form]);

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent asChild>
                <form onSubmit={handleSubmit}>
                    <PopoutHeader>
                        <PopoutTitle>
                            {user
                                ? t('dashboard.users.edit.title')
                                : t('dashboard.users.create.title')}
                        </PopoutTitle>
                    </PopoutHeader>
                    <Popout.Body>
                        <form.AppField
                            name="Settings.DisplayName"
                            validators={{
                                onChange: validators.createUserCommand.createUserCommandSettings.displayName,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.users.table.username')} />
                                    <field.Input
                                        placeholder={t('dashboard.users.table.usernamePlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Settings.SignatureText"
                            validators={{
                                onChange: validators.createUserCommand.createUserCommandSettings.signatureText,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.users.table.signatureText')} />
                                    <field.Input placeholder={t('dashboard.users.table.signatureTextPlaceholder')} />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <div className="flex gap-4">
                            <div className="basis-2/3">
                                <form.AppField
                                    name="Settings.FirstName"
                                    validators={{
                                        onChange: validators.createUserCommand.createUserCommandSettings.firstName,
                                    }}
                                    children={field => (
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.users.table.firstName')} />
                                            <field.Input
                                                placeholder={t('dashboard.users.table.firstNamePlaceHolder')}
                                            />
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                />
                            </div>
                            <div className="basis-1/3">
                                <form.AppField
                                    name="Settings.MiddleName"
                                    validators={{
                                        onChange: validators.createUserCommand.createUserCommandSettings.middleName,
                                    }}
                                    children={field => (
                                        <field.Label>
                                            <field.Label.Title label={t('dashboard.users.table.middleName')} />
                                            <field.Input
                                                placeholder={t('dashboard.users.table.middleNamerPlaceHolder')}
                                            />
                                            <field.ValidationError />
                                        </field.Label>
                                    )}
                                />
                            </div>
                        </div>

                        <form.AppField
                            name="Settings.LastName"
                            validators={{
                                onChange: validators.createUserCommand.createUserCommandSettings.lastName,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.users.table.lastName')} />
                                    <field.Input
                                        placeholder={t('dashboard.users.table.lastNamePlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Email"
                            validators={{
                                onChange: validators.createUserCommand.email,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.users.table.email')} />
                                    <field.Input
                                        placeholder={t('dashboard.users.table.emailPlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        {(!user || signature.signatureLoaded) && (
                            <form.AppField
                                name="File"
                                children={field => (
                                    <field.Label>
                                        <field.Label.Title label={!signature.selectedFile ? t('dashboard.users.table.signatureUpload') : t('dashboard.users.table.signatureFile')} />
                                        <div>
                                            {!signature.selectedFile && (
                                                <FileInput
                                                    maxFileSize={signature.validation?.MaxFileSize}
                                                    minFileSize={signature.validation?.MinFileSize}
                                                    maxFileName={signature.validation?.MaxFileNameLength}
                                                    onChange={(files) => {
                                                        signature.setSelectedFile(files[0]);
                                                        form.setFieldValue('File', files[0]);
                                                        signature.setUpdatedFile(true);
                                                        signature.setSignatureRemoved(false);
                                                    }}
                                                    accept="image/jpeg, image/png, image/webp, image/bmp, image/svg+xml"
                                                />
                                            )}
                                            {signature.selectedFile && (
                                                <div className="flex justify-center relative">
                                                    <img
                                                        className="max-h-32"
                                                        src={URL.createObjectURL(signature.selectedFile)}
                                                        alt="signature"
                                                    />
                                                    <Button
                                                        variant="ghost"
                                                        className="absolute right-0 top-1"
                                                        onClick={() => {
                                                            signature.setSelectedFile(null);
                                                            form.setFieldValue('File', null);
                                                            signature.setUpdatedFile(false);
                                                            signature.setSignatureRemoved(true);
                                                        }}
                                                    >
                                                        <Trash2 size={22} />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            />
                        )}

                        <form.AppField
                            name="Settings.PhoneNumber"
                            validators={{
                                onSubmit: ({ value }) => {
                                    if (!value) {
                                        return null;
                                    }

                                    const phoneRegex
                                        = /^(?:\+?(?:31|32)|0|6)\d{7,15}$/;
                                    if (!phoneRegex.test(value.replace(/\s+/g, ''))) {
                                        return t('dashboard.users.table.error.phoneInvalid');
                                    }

                                    return null;
                                },
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.users.table.phoneNumber')} />
                                    <PhoneInput value={field.state.value ?? ''} onChange={field.handleChange} countries={['NL', 'BE']} defaultCountry={field?.state?.value?.startsWith('+31') ? 'NL' : field?.state?.value?.startsWith('+32') ? 'BE' : 'NL'} />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="RoleIds"
                            validators={{
                                onChange: ({ value }) => value.length < 1 ? t('dashboard.users.popup.form.roles.error') : null,
                            }}
                            children={field => (
                                <field.Label asDiv>
                                    <field.Label.Title label={t('dashboard.users.table.role')} />
                                    <field.Popover>
                                        <field.CheckboxGroup options={roleOptions}>
                                            <field.Popover.Trigger>
                                                <field.CheckboxGroup.Trigger
                                                    placeholder={t('dashboard.users.table.rolePlaceholder')}
                                                    className="h-auto min-h-10 [&>div]:flex-col [&>div]:items-start [&>div]:gap-1"
                                                />
                                            </field.Popover.Trigger>
                                            <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" align="start">
                                                <field.CheckboxGroup options={roleOptions} />
                                            </field.Popover.Content>
                                        </field.CheckboxGroup>
                                    </field.Popover>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Settings.ColorCode"
                            children={field => (
                                <form.Subscribe
                                    selector={state => state.values.RoleIds}
                                >
                                    {(roleIds) => {
                                        const hasInspecteurRole = roleIds?.some(roleId =>
                                            organisationRoles?.isSuccess && (organisationRoles?.data?.Roles ?? []).some(
                                                role =>
                                                    role.RoleId === roleId
                                                    && role.Name && role.Name.toLowerCase().includes('inspecteur'),
                                            ),
                                        );

                                        return (
                                            <div className="flex flex-row justify-between">
                                                {hasInspecteurRole && (
                                                    <field.Label asDiv>
                                                        <field.Label.Title
                                                            label={t('dashboard.users.table.colorCodeInspecteur')}
                                                        />
                                                        <div className="flex flex-row gap-2">
                                                            {standardColors.map(color => (
                                                                <div
                                                                    key={color}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        field.handleChange(color);
                                                                    }}
                                                                    className={cn(
                                                                        `${field.state.value === color ? 'ring-1 ring-primary ring-offset-1' : ''} cursor-pointer rounded-full p-0.5`,
                                                                    )}
                                                                >
                                                                    <Observation color={color} label="" children />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </field.Label>
                                                )}
                                            </div>
                                        );
                                    }}
                                </form.Subscribe>
                            )}
                        />

                        <form.AppField
                            name="Settings.Department"
                            validators={{
                                onChange: validators.createUserCommand.createUserCommandSettings.department,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.users.table.department')} />
                                    <field.Input
                                        placeholder={t('dashboard.users.table.departmentPlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Settings.JobTitleDescription"
                            validators={{
                                onChange: validators.createUserCommand.createUserCommandSettings.jobTitleDescription,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.users.table.function')} />
                                    <field.Input
                                        placeholder={t('dashboard.users.table.functionPlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <div className="w-full flex flex-row gap-2"></div>
                    </Popout.Body>
                    <Popout.Footer className="flex flex-col gap-4">
                        <div className="w-full flex flex-row gap-2">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button disabled={loading} type="submit" variant="default" className="w-full">{!user ? t('dashboard.create') : t('dashboard.save')}</Button>
                        </div>
                    </Popout.Footer>
                </form>
            </PopoutContent>
        </Popout>
    );
}
